import React, {useCallback, useEffect, useState} from 'react';
import useApi from "../../utils/useApi";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import PrintableForm from "../PrintableForm/PrintableForm";
import SelectTaxYear from "../Admin/SelectTaxYear/SelectTaxYear";
import {useTaxYear} from "../../contexts/TaxYearContext";
import Button from "../UI/Button";

const TaxQuestionnaire = ({ user, admin, handleShowHeader, handleShowSettingsHeader }) => {
    const { getTaxQuestionnaire, getClientTaxQuestionnaire } = useApi();
    const { taxYear, updateTaxYear } = useTaxYear();

    const navigate = useNavigate();
    const { uid } = useParams();
    const [formsAvailable, setFormsAvailable] = useState([]);
    const [form, setForm] = useState(null);
    const [print, setPrint] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [customer, setCustomer] = useState(null);
    const [formFields, setFormFields] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const { saveForm, isLoading } = useApi();
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const initializeForm = useCallback(async () => {
        setLoading(true);
        try {
            const data = { year: taxYear };
            const response = uid
                             ? await getClientTaxQuestionnaire(uid, print, data)
                             : await getTaxQuestionnaire(data);

            // Batch state updates
            setForm(response.form);
            setFormsAvailable(response.formsAvailable);
            setCustomer(response.customer);

            // Initialize formFields with existing answers if any
            const initialFields = {};
            response.form.fields.forEach((field, index) => {
                initialFields[index] = {
                    form_field_id: field.id,
                    value: field.value || ''
                };
            });
            setFormFields(initialFields);
        } catch (error) {
            console.error('Error initializing form:', error);
        } finally {
            setLoading(false);
        }
    }, [taxYear]);


    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        initializeForm();
        console.log('Mounting');
    }, [taxYear]); // Separate UI setup effect



    const selectAllNo = () => {
        const newFormFields = { ...formFields };

        // Loop through all fields to find radio inputs
        form.fields.forEach((field, key) => {
            if (field.type === 'radio') {
                // Find the "no" option (case insensitive)
                const noOption = field.options.find(
                    option => option.value.toLowerCase() === 'no'
                );

                if (noOption) {
                    // Update the form field value
                    newFormFields[key] = {
                        form_field_id: field.id,
                        value: noOption.value
                    };

                    // If you want to also save each field to the backend
                    handleBlur(key, noOption.value, field.id);
                }
            }
        });

        setFormFields(newFormFields);
    };

    const selectAllYes = () => {
        const newFormFields = { ...formFields };

        // Loop through all fields to find radio inputs
        form.fields.forEach((field, key) => {
            if (field.type === 'radio') {
                // Find the "no" option (case insensitive)
                const noOption = field.options.find(
                    option => option.value.toLowerCase() === 'yes'
                );

                if (noOption) {
                    // Update the form field value
                    newFormFields[key] = {
                        form_field_id: field.id,
                        value: noOption.value
                    };

                    // If you want to also save each field to the backend
                    handleBlur(key, noOption.value, field.id);
                }
            }
        });

        setFormFields(newFormFields);
    };

    const handleYearChange = (year) => {
        const newYear = year;

        setSearchParams({ year: newYear });
    };

    const handleBlur = (key, value, fieldId) => {
        console.log(key, value);
        setFormFields(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                value: value
            }
        }));

        const updateField = {
            form_field_id: fieldId,
            value: value
        }

        const fields = [
            updateField,
        ];

        // Convert formFields to the expected format for the backend
        /*const fields = Object.values(formFields).map(field => ({
            form_field_id: field.form_field_id,
            value: field.value
        }));*/

        saveForm(customer.id, {
            form_id: form.id,
            fields: fields
        }).catch(console.error);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate your formFields object
        const errors = {};
        form.fields.forEach((field, key) => {
            if (field.required === 1 && !formFields[key]?.value) {
                errors[key] = "This field is required";
            }
        });

        if (Object.keys(errors).length > 0) {
            // Set error states and bail
            setFormErrors(errors);
            return;
        }

        setIsSaving(true);
        try {
            // Convert formFields to array format expected by backend
            const fields = Object.values(formFields).map(field => ({
                form_field_id: field.form_field_id,
                ...field
            }));

            const formData = {
                form_id: form.id,
                fields: fields
            };

            await saveForm(customer.id, formData);
            setIsSaving(false);
            navigate('/');
        } catch (error) {
            console.error('Error submitting form:', error);
        }
    };

    const handlePrintClose = () => {
        setPrint(false);
    }

    const handlePrint = () => {
        setPrint(true);
    }

    if(print){
        return <PrintableForm uid={customer.id} onPrintClose={handlePrintClose} />
    }

    const renderField = (field, key) => {
        const baseInputClasses = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100";

        switch (field.type) {
            case 'header':
                return <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100">{field.label}</h2>;

            case 'paragraph':
                return <div className="text-gray-700 dark:text-gray-300 mb-4" dangerouslySetInnerHTML={{ __html: field.label }} />;

            case 'textarea':
                return (
                    <div className="mb-4">
                        <label htmlFor={`field_${field.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {field.label}
                        </label>
                        <textarea
                            className={baseInputClasses}
                            id={`field_${field.id}`}
                            defaultValue={formFields[key]?.value || ''}
                            rows="3"
                            required={field.required === 1}
                            onChange={(e) => handleBlur(key, e.target.value, field.id)}
                        />
                    </div>
                );

            case 'checkbox':
                return (
                    <div className="mb-4 flex items-center">
                        <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                            id={`field_${field.id}`}
                            defaultChecked={formFields[key]?.value === '1'}
                            required={field.required === 1}
                            onChange={(e) => handleBlur(key, e.target.checked ? '1' : '0', field.id)}
                        />
                        <label htmlFor={`field_${field.id}`} className="ml-2 block text-sm text-gray-700 dark:text-gray-300 capitalize">
                            {field.label}
                        </label>
                    </div>
                );

            case 'text':
            case 'date':
            case 'email':
                return (
                    <div className="mb-4">
                        <label htmlFor={`field_${field.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {field.label}
                        </label>
                        <input
                            className={baseInputClasses}
                            type={field.type}
                            id={`field_${field.id}`}
                            defaultValue={formFields[key]?.value || ''}
                            required={field.required === 1}
                            onChange={(e) => handleBlur(key, e.target.value, field.id)}
                        />
                    </div>
                );

            case 'select':
                return (
                    <div className="mb-4">
                        <label htmlFor={`field_${field.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {field.label}
                        </label>
                        <select
                            className={`${baseInputClasses} capitalize`}
                            id={`field_${field.id}`}
                            required={field.required === 1}
                            defaultValue={formFields[key]?.value || ''}
                            onChange={(e) => handleBlur(key, e.target.value, field.id)}
                        >
                            <option value="">
                                Select
                            </option>
                            {field.options.map((option) => (
                                <option key={option.value} value={option.value} className="capitalize">
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                );

            case 'checkboxlist':
                return (
                    <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{field.label}</label>
                        <div className="space-y-2">
                            {field.options.map((option) => (
                                <div key={option.value} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700"
                                        id={`field_${field.id}_${option.value}`}
                                        name={`field_${field.id}`}
                                        required={field.required == 1}
                                        defaultChecked={formFields[key]?.value === option.value}
                                        value={option.value}
                                        onChange={(e) => handleBlur(key, e.target.checked ? option.value : '', field.id)}
                                    />
                                    <label className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize" htmlFor={`field_${field.id}_${option.value}`}>
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            case 'radio':
                return (
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{field.label}</label>
                        <div className="space-y-2">
                            {field.options.map((option) => (
                                <div key={option.value} className="flex items-center">
                                    <input
                                        type="radio"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                                        id={`field_${field.id}_${option.value}`}
                                        name={`field_${field.id}`}
                                        required={field.required === 1}
                                        defaultChecked={formFields[key]?.value === option.value}
                                        value={option.value}
                                        onChange={(e) => handleBlur(key, option.value, field.id)}
                                    />
                                    <label className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize" htmlFor={`field_${field.id}_${option.value}`}>
                                        {option.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    if (loading) {
        return <LoadingScreen />;
    }



    return (
        <div className="relative z-10 page-container sm:px-6 lg:px-8">
            <header className="bg-white dark:bg-gray-800 shadow mx-auto rounded-lg shadow-md dark:shadow-gray-900/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-row justify-between">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        Tax Questionnaire
                        {admin && (
                            <Link to={`/clients/${customer.id}`} className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                {customer.profile.client}
                            </Link>
                        )}
                    </h3>
                    <div className="w-full md:w-auto">
                        <SelectTaxYear selectedYear={taxYear} availableYears={formsAvailable} customText={true} onSelectYear={handleYearChange} />
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto py-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                    <div className="p-6">
                        {admin && (
                            <a
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
                                href="#"
                                onClick={handlePrint}
                            >
                                Print
                            </a>
                        )}

                        <form onSubmit={handleSubmit} className="mt-6">
                            {form && <input type="hidden" name="form_id" value={form.id} />}
                            <div className="max-w-3xl">
                                {form.fields.map((field, key) => (
                                    <div
                                        key={key}
                                        className={`bg-gray-50 dark:bg-gray-700 rounded-lg p-6 mb-6 ${field.sectionCount ? 'mt-12 rounded-t-none' : ''} relative`}
                                    >
                                        {field.sectionCount && (
                                            <div className="absolute -top-10 left-0 w-full">
                                                <div className="bg-gray-800 dark:bg-gray-900 text-white px-4 py-2 rounded-t-lg">
                                                    {field.section}
                                                </div>
                                            </div>
                                        )}
                                        <input
                                            type="hidden"
                                            name={`field[${key}][form_field_id]`}
                                            value={field.id}
                                        />
                                        {renderField(field, key)}
                                        {formErrors[key] && (
                                            <span className="text-red-500 text-sm">
                                                {formErrors[key]}
                                              </span>
                                        )}
                                    </div>
                                ))}

                                {/*
                                <div className="mt-8">
                                    <Button
                                        onClick={selectAllNo}
                                    >
                                        Set All To No
                                    </Button>
                                </div>



                                <div className="mt-8">
                                    <button
                                        onClick={selectAllYes}
                                        className={`
                                         px-6 py-2 rounded text-white

                                         transition-colors duration-200
                                     `}
                                    >
                                        Set All To Yes
                                    </button>
                                </div>
                                */}
                                <div className="mt-8">
                                    <button
                                        disabled={isSaving}
                                        className={`
                                         px-6 py-2 rounded text-white
                                         ${isSaving ?
                                           'bg-blue-400 cursor-not-allowed animate-pulse' :
                                           'bg-blue-500 hover:bg-blue-600'
                                        }
                                         transition-colors duration-200
                                     `}
                                    >
                                        {isSaving ? 'Processing...' : 'Save Form'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TaxQuestionnaire;
